import { useMemo } from 'react';
import { ahZoekUrl, bouwAhBestelUrl, toonAantal } from '@boodschappenlijst/shared';
import { zoekAhId } from '../../lib/ahCatalogus';
import type { ItemMetSamenvoegingen } from './items.api';

interface Props {
  items: ItemMetSamenvoegingen[];
}

/**
 * Zet de openstaande lijst in één klik in het AH-mandje.
 *
 * Alles wat in de productcatalogus staat heeft een AH-productnummer en gaat
 * automatisch mee. Voor de rest — vrij ingetypte regels, of producten waar we
 * het nummer nog niet van hebben — tonen we een zoeklink, zodat duidelijk is
 * wat er nog met de hand bij moet.
 */
export function BestelBijAh({ items }: Props) {
  const { url, gekoppeld, handmatig } = useMemo(() => {
    const open = items.filter((i) => !i.gedaan);
    const gekoppeld: { item: ItemMetSamenvoegingen; ahId: number }[] = [];
    const handmatig: ItemMetSamenvoegingen[] = [];

    for (const item of open) {
      const ahId = zoekAhId(item.naam);
      if (ahId) gekoppeld.push({ item, ahId });
      else handmatig.push(item);
    }

    const url = bouwAhBestelUrl(gekoppeld.map(({ item, ahId }) => ({ ahId, aantal: item.aantal })));
    return { url, gekoppeld, handmatig };
  }, [items]);

  if (!url && handmatig.length === 0) return null;

  return (
    <section>
      <h2 className="vs-section-title">Bestellen</h2>
      <div className="vs-card vs-bestel">
        {url ? (
          <>
            <a className="vs-bestel__knop" href={url} target="_blank" rel="noopener noreferrer">
              {/* Leeg alt: het logo zegt niets wat de knoptekst niet al zegt,
                  en "AH-logo" laten voorlezen voor "AH-mandje" is dubbelop. */}
              <img className="vs-bestel__logo" src="/ah-logo.png" alt="" width={30} height={30} />
              <span>
                Zet {gekoppeld.length} {gekoppeld.length === 1 ? 'product' : 'producten'} in je{' '}
                {/* Bij elkaar houden: op een smal scherm breekt de regel anders
                    op het streepje, en dan staat er "AH-" boven "mandje". */}
                <span className="vs-bestel__merk">AH-mandje</span>
              </span>
              <svg
                className="vs-bestel__pijl"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 10 10 4" />
                <path d="M4.6 4H10v5.4" />
              </svg>
            </a>
            <p className="vs-bestel__uitleg">
              Opent ah.nl met de producten al in je mandje. Bestellen en betalen doe je daar.
            </p>
          </>
        ) : (
          <p className="vs-bestel__uitleg">
            Geen van de producten op de lijst is aan een AH-product gekoppeld.
          </p>
        )}

        {handmatig.length > 0 && (
          <div className="vs-bestel__rest">
            <p className="vs-bestel__uitleg">
              {/* Bewust niet "staat niet in de productlijst": een regel kan er wél
                  in staan maar nog geen AH-productnummer hebben. Het aantal
                  stuurt de aanwijzende voornaamwoorden, niet het werkwoord —
                  onderwerp is "we", dus dat blijft "konden". */}
              {handmatig.length === 1 ? 'Dit' : 'Deze'} konden we niet aan een AH-product koppelen,{' '}
              {handmatig.length === 1 ? 'dat' : 'die'} zoek je zelf even op:
            </p>
            <ul className="vs-bestel__lijst">
              {handmatig.map((item) => (
                <li key={item.id}>
                  <a href={ahZoekUrl(item.naam)} target="_blank" rel="noopener noreferrer">
                    {item.naam}
                  </a>{' '}
                  <span className="vs-bestel__aantal">{toonAantal(item.aantal, item.eenheid)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
